import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraFacing = 'user' | 'environment';

export interface UseCameraReturn {
  videoRef: React.RefObject<HTMLVideoElement>;
  isStreaming: boolean;
  isStarting: boolean;
  error: string | null;
  facing: CameraFacing;
  startCamera: (facingMode?: CameraFacing) => Promise<void>;
  stopCamera: () => void;
  switchCamera: () => Promise<void>;
  takePicture: () => Blob | null;
}

export function useCamera(): UseCameraReturn {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facing, setFacing] = useState<CameraFacing>('user');

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  const startCamera = useCallback(async (facingMode: CameraFacing = 'user') => {
    setError(null);
    setIsStarting(true);

    // Stop any existing stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setFacing(facingMode);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsStreaming(true);
      }
    } catch (err: any) {
      const msg =
        err?.name === 'NotAllowedError'
          ? "Accès à la caméra refusé. Veuillez autoriser l'accès dans les paramètres du navigateur."
          : err?.name === 'NotFoundError'
            ? 'Aucune caméra trouvée sur cet appareil.'
            : err?.name === 'NotReadableError'
              ? 'La caméra est déjà utilisée par une autre application.'
              : `Impossible d'accéder à la caméra : ${err?.message || 'Erreur inconnue'}`;
      setError(msg);
    } finally {
      setIsStarting(false);
    }
  }, []);

  const switchCamera = useCallback(async () => {
    const next: CameraFacing = facing === 'user' ? 'environment' : 'user';
    await startCamera(next);
  }, [facing, startCamera]);

  const takePicture = useCallback((): Blob | null => {
    const video = videoRef.current;
    if (!video || !isStreaming) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Mirror the image if using front camera so the result matches what the user saw
    if (facing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob synchronously via toDataURL then back to Blob
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const byteString = atob(dataUrl.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: 'image/jpeg' });
  }, [isStreaming, facing]);

  // Clean up stream when hook unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    videoRef,
    isStreaming,
    isStarting,
    error,
    facing,
    startCamera,
    stopCamera,
    switchCamera,
    takePicture,
  };
}
